import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "react-bootstrap/Spinner";
import { userLogin, checkLogin } from "features/common/auth/authSlice";
import Modal from "react-bootstrap/Modal";
import ModalForgotPassword from "layouts/ModalForgotPassword";
import { notifyWarning, notifyError } from "components/ToastMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { getBrowserInfo } from "helpers/browserDetection";

const ModalLogin = (props) => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [modalForgot, setModalForgot] = useState(false);
  const isLoading = useSelector((state) => state.auth.status);
  const { twoFactorEnabled, browserInfo } = useSelector((state) => state.auth);
  const status_login = useSelector((state) => state.auth.status_login);
  const authError = useSelector((state) => state.auth.error);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [kode2FA, setKode2FA] = useState("");
  const [loginDisable, setLoginDisable] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [urlIframe, setUrlIframe] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // OTP Verification states
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpRemainingTime, setOtpRemainingTime] = useState(0);
  const [otpCountdown, setOtpCountdown] = useState(null);
  const [otpResendDisabled, setOtpResendDisabled] = useState(false);

  // Tambahkan state untuk error
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    kode2FA: "",
    otp: ""
  });

  // Fungsi validasi email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "Email tidak boleh kosong";
    }
    if (!emailRegex.test(email)) {
      return "Format email tidak valid";
    }
    return "";
  };

  // Fungsi validasi password
  const validatePassword = (password) => {
    if (!password) {
      return "Password tidak boleh kosong";
    }
    if (password.length < 8) {
      return "Password minimal 8 karakter";
    }
    return "";
  };

  // Fungsi validasi kode 2FA
  const validate2FACode = (code) => {
    if (!code) {
      return "Kode 2FA tidak boleh kosong";
    }
    if (!/^\d{6}$/.test(code)) {
      return "Kode 2FA harus 6 digit angka";
    }
    return "";
  };

  // Fungsi validasi OTP
  const validateOTP = (code) => {
    if (!code) {
      return "Kode OTP tidak boleh kosong";
    }
    if (!/^\d{6}$/.test(code)) {
      return "Kode OTP harus 6 digit angka";
    }
    return "";
  };

  // Handler untuk perubahan email
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setErrors(prev => ({
      ...prev,
      email: validateEmail(value)
    }));
  };

  // Handler untuk perubahan password
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setErrors(prev => ({
      ...prev,
      password: validatePassword(value)
    }));
  };

  // Handler untuk perubahan kode 2FA
  const handle2FAChange = (e) => {
    const value = e.target.value;
    setKode2FA(value);
    setErrors(prev => ({
      ...prev,
      kode2FA: validate2FACode(value)
    }));
  };

  // Handler untuk perubahan OTP
  const handleOTPChange = (e) => {
    const value = e.target.value;
    setOtpCode(value);
    setErrors(prev => ({
      ...prev,
      otp: validateOTP(value)
    }));
  };

  const handlerLogin = (e) => {
    e.preventDefault();

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    setErrors({
      email: emailError,
      password: passwordError,
      kode2FA: ""
    });

    if (emailError || passwordError) {
      return;
    }

    setLoginDisable(true);
    // console.log('Attempting login with:', { email }); // Log login attempt
    dispatch(userLogin([email, password]));
  };

  const handlerLogin2FA = (e) => {
    e.preventDefault();
    const code2FAError = validate2FACode(kode2FA);
    setErrors(prev => ({ ...prev, kode2FA: code2FAError }));
    if (code2FAError) return;

    setLoginDisable(true);
    setShowIframe(true);
    setUrlIframe(`${process.env.REACT_APP_BASE_API}login/challenge?kode=${kode2FA}`);
  };

  // Handler untuk verifikasi OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpError = validateOTP(otpCode);
    setErrors(prev => ({ ...prev, otp: otpError }));
    if (otpError) return;

    setLoginDisable(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/verifyLoginOtp.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: new URLSearchParams({
          otp: otpCode,
          email: email
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // OTP berhasil diverifikasi
        setShowOtpVerification(false);
        setOtpCode("");
        setLoginDisable(false);
        // Dispatch checkLogin to update auth state
        dispatch(checkLogin());
        props.onHide();
      } else {
        setLoginDisable(false);
        notifyError(data.error || "Verifikasi OTP gagal");
        setOtpCode("");
      }
    } catch (error) {
      setLoginDisable(false);
      notifyError("Terjadi kesalahan saat verifikasi OTP");
      console.error('OTP verification error:', error);
    }
  };

  // Handler untuk resend OTP
  const handleResendOTP = async () => {
    setOtpResendDisabled(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/resendLoginOtp.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: new URLSearchParams({
          email: email
        })
      });

      const data = await response.json();
      
      if (data.success) {
        notifyWarning("Kode OTP baru telah dikirim ke email Anda");
        setOtpRemainingTime(180); // 3 minutes
        startOtpCountdown();
      } else {
        notifyError(data.error || "Gagal mengirim ulang OTP");
      }
    } catch (error) {
      notifyError("Terjadi kesalahan saat mengirim ulang OTP");
      console.error('Resend OTP error:', error);
    }
    
    // Re-enable resend button after 30 seconds
    setTimeout(() => {
      setOtpResendDisabled(false);
    }, 30000);
  };

  // Fungsi untuk memulai countdown OTP
  const startOtpCountdown = () => {
    if (otpCountdown) {
      clearInterval(otpCountdown);
    }
    
    const countdown = setInterval(() => {
      setOtpRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          setOtpCountdown(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setOtpCountdown(countdown);
  };

  // Fungsi untuk format waktu countdown
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Reset form and error when modal is closed
  useEffect(() => {
    if (!props.show) {
      setEmail("");
      setPassword("");
      setKode2FA("");
      setOtpCode("");
      setShowOtpVerification(false);
      setErrors({
        email: "",
        password: "",
        kode2FA: "",
        otp: ""
      });
      setLoginDisable(false);
      // setShowIframe(false);
      setRetryCount(0);
      setOtpRemainingTime(0);
      if (otpCountdown) {
        clearInterval(otpCountdown);
        setOtpCountdown(null);
      }
    }
  }, [props.show, otpCountdown]);

  useEffect(() => {
    if (isLoading !== "loading") {
      setLoginDisable(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (status_login && props.show) {
      props.onHide();
    }
  }, [status_login, props]);

  // Show warning if browser doesn't support 2FA
  useEffect(() => {
    if (twoFactorEnabled && browserInfo && !browserInfo.supports2FA) {
      notifyWarning("Browser Anda mungkin tidak mendukung fitur 2FA dengan baik. Silakan gunakan browser terbaru.");
    }
  }, [twoFactorEnabled, browserInfo]);

  useEffect(() => {
    if (!showIframe) return;

    let timeoutId;
    let fallbackTimeoutId;
    let authCheckInterval;
    const browserInfo = getBrowserInfo();
    let isSafari = browserInfo.name === 'safari' || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // Safari-specific detection using postMessage
    const handleMessage = (event) => {
      const base = process.env.REACT_APP_BASE_API;

      // Verify origin for security
      if (!event.origin.startsWith(base.replace(/\/$/, ''))) return;

      if (event.data === 'login_success') {
        setShowIframe(false);
        setLoginDisable(false);
        clearTimeout(timeoutId);
        clearTimeout(fallbackTimeoutId);
        clearInterval(authCheckInterval);
        // Dispatch checkLogin to update auth state without reload
        dispatch(checkLogin());
      } else if (event.data === 'login_failed' || event.data === 'incorrect_code') {
        setShowIframe(false);
        setLoginDisable(false);
        setKode2FA("");
        notifyWarning("Kode 2FA tidak sesuai! Silahkan coba lagi.");
        clearTimeout(timeoutId);
        clearTimeout(fallbackTimeoutId);
        clearInterval(authCheckInterval);
      }
    };

    // Add message listener for Safari compatibility
    window.addEventListener('message', handleMessage);

    // For Safari: Use alternative detection method via auth status checking
    if (isSafari) {
      authCheckInterval = setInterval(async () => {
        try {
          // Check if user is authenticated by trying to access a protected resource
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout per request

          const response = await fetch(`${process.env.REACT_APP_BASE_API}clientarea.php`, {
            method: 'HEAD', // Use HEAD to reduce bandwidth
            credentials: 'include',
            mode: 'cors',
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          // If response is successful and not redirected to login page
          if (response.ok && !response.url.includes('login')) {
            setShowIframe(false);
            setLoginDisable(false);
            clearInterval(authCheckInterval);
            clearTimeout(fallbackTimeoutId);
            // Dispatch checkLogin to update auth state without reload
            dispatch(checkLogin());
          }
        } catch (error) {
          // Network error or blocked request - continue checking
        }
      }, 3000); // Check every 3 seconds for Safari
    }

    const interval = setInterval(() => {
      const iframe = document.getElementById("iframe_2fa");
      if (iframe && iframe.contentWindow) {
        let iframeUrl;
        try {
          iframeUrl = iframe.contentWindow.location.href;
        } catch (e) {
          // Cross-origin error - common in Safari
          // Skip URL checking for Safari, rely on authCheckInterval
          if (isSafari) {
            return;
          }
          return;
        }

        const base = process.env.REACT_APP_BASE_API;

        // Check for successful login (clientarea.php)
        if (iframeUrl.startsWith(`${base}clientarea.php`)) {
          setShowIframe(false);
          setLoginDisable(false);
          clearInterval(interval);
          clearInterval(authCheckInterval);
          clearTimeout(timeoutId);
          clearTimeout(fallbackTimeoutId);
          // Dispatch checkLogin to update auth state without reload
          dispatch(checkLogin());
        }
        // Check for OTP verification redirect
        else if (iframeUrl.includes('otp_verification.php')) {
          setShowIframe(false);
          setLoginDisable(false);
          setShowOtpVerification(true);
          // Start OTP countdown
          setOtpRemainingTime(180);
          // Start countdown
          const countdown = setInterval(() => {
            setOtpRemainingTime(prev => {
              if (prev <= 1) {
                clearInterval(countdown);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          setOtpCountdown(countdown);
          clearInterval(interval);
          clearInterval(authCheckInterval);
          clearTimeout(timeoutId);
          clearTimeout(fallbackTimeoutId);
        }
        // Check for incorrect code
        else if (iframeUrl.startsWith(`${base}login/challenge?incorrect=1`)) {
          setShowIframe(false);
          setLoginDisable(false);
          setKode2FA("");
          notifyWarning("Kode 2FA tidak sesuai! Silahkan coba lagi.");
          clearInterval(interval);
          clearInterval(authCheckInterval);
          clearTimeout(timeoutId);
          clearTimeout(fallbackTimeoutId);
        }
      }
    }, 1000);

    // Fallback timeout - if no response after 45 seconds (longer for Safari)
    fallbackTimeoutId = setTimeout(() => {
      setShowIframe(false);
      setLoginDisable(false);
      setKode2FA("");
      notifyError("Timeout: Proses login memakan waktu terlalu lama. Silahkan coba lagi.");
      clearInterval(interval);
      clearInterval(authCheckInterval);
    }, 45000); // 45 second timeout

    return () => {
      clearInterval(interval);
      clearInterval(authCheckInterval);
      clearTimeout(timeoutId);
      clearTimeout(fallbackTimeoutId);
      window.removeEventListener('message', handleMessage);
    };
  }, [showIframe, dispatch]);

  // Handle auth errors - add this useEffect
  useEffect(() => {
    if (isLoading === "error" && props.show && authError) {
      setLoginDisable(false);
      
      // Handle different error structures
      let errorMessage = "Login gagal. Silakan coba lagi.";
      
      if (typeof authError === 'string') {
        errorMessage = authError;
      } else if (authError.message) {
        errorMessage = authError.message;
      } else if (authError.error && typeof authError.error === 'string') {
        errorMessage = authError.error;
      } else if (authError.error && authError.error.message) {
        errorMessage = authError.error.message;
      }
      
      // Show notification
      notifyError(errorMessage);
      
      // Reset form if needed
      setPassword("");
      setKode2FA("");
    }
  }, [isLoading, authError, props.show]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (otpCountdown) {
        clearInterval(otpCountdown);
      }
    };
  }, [otpCountdown]);

  return (
    <>
      {/* modal for login button */}
      <Modal
        show={props.show}
        onHide={props.onHide}
        backdrop="static"
        keyboard={false}
        size="sm"
        aria-labelledby="example-custom-modal-styling-title"
        centered
      >
        {showOtpVerification ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title id="example-custom-modal-styling-title">
                Verifikasi OTP
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <form name="otpForm" autoComplete="on">
                <div className="form">
                  <div className="mb-3">
                    <h5 className="text-start mb-3">Akses dari Perangkat Baru?</h5>
                    <p className="text-start mb-3" style={{ fontSize: '14px' }}>
                      Selamat datang kembali! Kami melihat bahwa Anda mengakses situs web kami dari perangkat baru. 
                      Untuk memastikan keamanan akun Anda, kami mengambil langkah tambahan untuk memverifikasi login ini.
                    </p>
                    
                    <div className="mb-3">
                      <h6 className="text-start mb-2">Instruksi:</h6>
                      <ol className="text-start" style={{ fontSize: '14px', paddingLeft: '20px' }}>
                        <li><strong>Periksa EMAIL Anda:</strong> Kami telah mengirimkan kode OTP melalui EMAIL ke alamat email yang terdaftar. Kode OTP ini akan kedaluwarsa dalam 3 menit.</li>
                        <li><strong>Buka EMAIL Anda</strong> dan cari pesan dari Qwords, kemudian salin <strong>Kode OTP</strong> dari email tersebut dan tempelkan pada formulir di bawah ini.</li>
                        <li>Jika Anda tidak memulai login ini, hubungi tim support kami.</li>
                      </ol>
                    </div>

                    {otpRemainingTime > 0 && (
                      <div className="mb-3">
                        <p className="text-center mb-2" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                          Time remaining: {formatTime(otpRemainingTime)}
                        </p>
                      </div>
                    )}

                    {otpRemainingTime === 0 && (
                      <div className="mb-3">
                        <p className="text-center text-danger mb-2" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                          The OTP has expired. Please request a new one.
                        </p>
                      </div>
                    )}

                    <label htmlFor="otpCode" className="form-label label-qwords">
                      Masukkan OTP:
                    </label>
                    <input
                      type="number"
                      className={`form-control input-modal ${errors.otp ? 'is-invalid' : ''}`}
                      id="otpCode"
                      placeholder="Masukkan Kode OTP Anda"
                      onChange={handleOTPChange}
                      value={otpCode}
                      maxLength={6}
                      disabled={otpRemainingTime === 0}
                    />
                    {errors.otp && (
                      <div className="invalid-feedback">{errors.otp}</div>
                    )}
                  </div>
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
              <div className="row gap-3 w-100">
                <button
                  className="btn btn-modal-login col-lg-12"
                  onClick={handleVerifyOTP}
                  disabled={loginDisable || !!errors.otp || otpRemainingTime === 0}
                >
                  {isLoading === "loading" || loginDisable ? (
                    <Spinner animation="border" />
                  ) : (
                    "Verifikasi OTP"
                  )}
                </button>
                
                <button
                  className="btn btn-outline-secondary col-lg-12"
                  onClick={handleResendOTP}
                  disabled={otpResendDisabled || loginDisable}
                >
                  {otpResendDisabled ? "Tunggu 30 detik..." : "Kirim Ulang OTP"}
                </button>
              </div>
            </Modal.Footer>
          </>
        ) : twoFactorEnabled ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title id="example-custom-modal-styling-title">
                Kode 2FA
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <form name="twofaForm" autoComplete="on">
                <div className="form">
                  <div className="mb-3">
                    <label htmlFor="kode2FA" className="form-label label-qwords">
                      Masukkan Kode 2FA
                    </label>
                    <input
                      type="number"
                      className={`form-control input-modal ${errors.kode2FA ? 'is-invalid' : ''}`}
                      id="kode2FA"
                      placeholder="Masukkan Kode 2FA Anda"
                      onChange={handle2FAChange}
                      value={kode2FA}
                      maxLength={6}
                    />
                    {errors.kode2FA && (
                      <div className="invalid-feedback">{errors.kode2FA}</div>
                    )}
                    {retryCount > 0 && (
                      <small className="text-warning">
                        Sisa percobaan: {MAX_RETRIES - retryCount}
                      </small>
                    )}
                  </div>
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
              <div className="row gap-3 w-100">
                <button
                  className="btn btn-modal-login col-lg-12"
                  onClick={handlerLogin2FA}
                  disabled={loginDisable || !!errors.kode2FA}
                >
                  {isLoading === "loading" || loginDisable ? (
                    <Spinner animation="border" />
                  ) : (
                    "Masuk"
                  )}
                </button>
              </div>
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Header closeButton>
              <Modal.Title id="example-custom-modal-styling-title">
                Login
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <form name="loginForm" autoComplete="on">
                <div className="form">
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label label-qwords">
                      Masukkan Email
                    </label>
                    <input
                      type="email"
                      className={`form-control input-modal ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      aria-describedby="emailHelp"
                      placeholder="Masukkan Email Anda"
                      onChange={handleEmailChange}
                      value={email}
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label label-qwords">
                      Masukkan Password
                    </label>
                    <div className="input-group mb-3">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`form-control input-modal ${errors.password ? 'is-invalid' : ''}`}
                        aria-describedby="show-password"
                        id="password"
                        placeholder="Minimal 8 karakter"
                        onChange={handlePasswordChange}
                        value={password}
                      />
                      <button
                        className="input-group-text"
                        id="show-password"
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? faEyeSlash : faEye}
                          className="text-muted"
                        />
                      </button>
                      {errors.password && (
                        <div className="invalid-feedback">{errors.password}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", color: "#ee4d30" }}>
                    <p
                      className="text-decoration-none"
                      style={{ color: "#ee4d30", cursor: "pointer" }}
                      onClick={() => {
                        setModalForgot(true);
                        props.onClickLogin(false);
                      }}
                      role="button"
                    >
                      Lupa Password?
                    </p>
                  </div>
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
              <div className="row gap-3 w-100">
                <button
                  className="btn btn-modal-login col-lg-12"
                  onClick={handlerLogin}
                  disabled={loginDisable || !!errors.email || !!errors.password}
                >
                  {isLoading === "loading" ? (
                    <Spinner animation="border" />
                  ) : (
                    "Masuk"
                  )}
                </button>
                <div className="col-lg-12">
                  <div className="d-flex align-items-center">
                    <hr className="flex-grow-1 mb-0" style={{ height: "1px" }} />
                    <span className="mx-3 text-muted small">atau</span>
                    <hr className="flex-grow-1 mb-0" style={{ height: "1px" }} />
                  </div>
                </div>
                <button
                  className="btn btn-modal-outline-daftar col-lg-12"
                  disabled={loginDisable}
                  onClick={() => {
                    props.onClickLogin(false);
                    props.onClickDaftar(true);
                  }}
                >
                  Daftar
                </button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <ModalForgotPassword
        show={modalForgot}
        onHide={() => setModalForgot(false)}
        onClickForgot={setModalForgot}
      />

      {showIframe && (
        <>
          <iframe
            src={urlIframe}
            width="300"
            height="300"
            title="2FA Iframe"
            id="iframe_2fa"
            style={{
              position: "fixed",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "none",
              zIndex: 9999,
              background: "#fff",
              boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              borderRadius: "8px"
            }}
          />

          {/* Safari indicator */}
          {/^((?!chrome|android).)*safari/i.test(navigator.userAgent) && (
            <div style={{
              position: "fixed",
              top: "45%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(0,0,0,0.8)",
              color: "white",
              padding: "10px 15px",
              borderRadius: "5px",
              fontSize: "12px",
              zIndex: 10000,
              textAlign: "center",
              maxWidth: "280px"
            }}>
              🍎 Safari: Memproses autentikasi...
              <br />
              <small>Mohon tunggu hingga proses selesai</small>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ModalLogin;
